import type {
	DeepPartial,
	LanguageKey,
	NubeSDKRuntime,
	NubeSDKRuntimeListener,
	NubeSDKRuntimeState,
} from "@tiendanube/nube-sdk-internal-types";
import deepcopy from "deepcopy";
import { deepMerge } from "./deep-merge";

const FULL_REPLACE_PATHS = ["location.page.data", "ui.slots"];

function buildAppUiSlotsRoute(appid: string): string {
	return `apps.${appid}.ui.slots`;
}

const EMPTY_STATE: NubeSDKRuntimeState = {
	cart: {
		id: "",
		validation: { status: "pending" },
		items: [],
		prices: {
			discount_coupon: 0,
			discount_gateway: 0,
			discount_promotion: 0,
			subtotal_without_taxes: 0,
			shipping: 0,
			subtotal: 0,
			total: 0,
		},
		// TODO: fix type check
		// @ts-ignore
		coupon: null,
	},
	config: {
		has_cart_validation: false,
		disable_shipping_more_options: false,
	},
	location: {
		url: "",
		// TODO: fix type check
		// @ts-ignore
		page: {},
		queries: {},
	},
	store: {
		id: 0,
		name: "",
		domain: "",
		currency: "",
		language: "" as LanguageKey,
	},
	ui: {
		values: {},
		slots: {},
	},
	apps: {},
	shipping: {
		selected: null,
		options: [],
		custom_labels: {},
	},
	// TODO: fix type check
	// @ts-ignore
	customer: {
		contact: {
			email: null,
			name: null,
			phone: null,
			accepts_marketing: null,
			accepts_marketing_updated_at: null,
		},
		shipping_address: {
			zipcode: "",
			first_name: null,
			last_name: null,
			address: null,
			number: null,
			floor: null,
			locality: null,
			city: null,
			state: null,
			country: null,
			phone: null,
			between_street: null,
			reference: null,
		},
		billing_address: {
			zipcode: "",
			first_name: null,
			last_name: null,
			address: null,
			number: null,
			floor: null,
			locality: null,
			city: null,
			state: null,
			country: null,
			phone: null,
			id_number: null,
			customer_type: null,
			business_name: null,
			trade_name: null,
			state_registration: null,
			fiscal_regime: null,
			invoice_use: null,
			document_type: null,
			business_activity: null,
		},
	},
	payment: {
		status: null,
		selected: null,
	},
};

export function createSDK(
	initialData: DeepPartial<NubeSDKRuntimeState>,
): NubeSDKRuntime {
	const handlers = new Map<string, NubeSDKRuntimeListener[]>();
	let data: NubeSDKRuntimeState =
		deepMerge(EMPTY_STATE, initialData, FULL_REPLACE_PATHS) ?? EMPTY_STATE;

	return {
		on(event, listener) {
			const listeners = handlers.get(event) || [];
			handlers.set(event, [...listeners, listener]);
		},

		off(event, listener) {
			const listeners = handlers.get(event);

			if (listeners === undefined) return;

			handlers.set(
				event,
				listeners.filter((fn) => fn !== listener),
			);
		},

		send(appid, event, modifier, target) {
			// whenever an event is sent the eventPayload is cleared
			data.eventPayload = null;

			if (modifier) {
				const result = deepMerge(data, modifier(data), [
					...FULL_REPLACE_PATHS,
					buildAppUiSlotsRoute(appid),
				]);
				if (result) data = result;
			}

			const eventListeners = handlers.get(event);

			if (eventListeners)
				for (const fn of eventListeners) fn(data, event, appid, target);

			const globalListeners = handlers.get("*");

			if (globalListeners)
				for (const fn of globalListeners) fn(data, event, appid, target);
		},

		getState() {
			return deepcopy(data);
		},
	};
}
